import { useEffect, useState } from "react";
import type { PortfolioCase } from "@/mocks/portfolio";
import { portfolioCases, methodFilters } from "@/mocks/portfolio";

interface Props {
  item: PortfolioCase | null;
  onClose: () => void;
}

export default function PortfolioDetailModal({ item, onClose }: Props) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (item) {
      requestAnimationFrame(() => setVisible(true));
      document.body.style.overflow = "hidden";
    } else {
      setVisible(false);
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [item]);

  if (!item) return null;

  const d = item.detail;
  const methodLabel = methodFilters.find((m) => m.value === item.method)?.label;

  // Find similar cases (same tagKey or same method, excluding current)
  const similarCases = portfolioCases
    .filter((c) => c.id !== item.id)
    .filter(
      (c) =>
        c.tagKey === item.tagKey ||
        (item.method && c.method === item.method)
    )
    .slice(0, 3);

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-3 md:p-6 transition-all duration-300 ${
        visible ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      onClick={onClose}
      aria-modal="true"
      role="dialog"
    >
      <div className="absolute inset-0 bg-graphite/60 backdrop-blur-sm" />

      <div
        className={`relative bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl transition-all duration-300 ${
          visible ? "scale-100 translate-y-0" : "scale-95 translate-y-4"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white/90 backdrop-blur-sm text-graphite hover:bg-champagne hover:text-white transition-all duration-200 cursor-pointer shadow-sm"
          aria-label="關閉彈窗"
        >
          <i className="ri-close-line text-xl" />
        </button>

        <div className="relative h-56 md:h-72 overflow-hidden rounded-t-2xl">
          <img
            src={item.image}
            alt={item.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-graphite/70 via-transparent to-transparent" />
          <div className="absolute bottom-4 left-6 right-6">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="tag-label">{item.tag}</span>
              {methodLabel && (
                <span
                  className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                    item.method === "prefab"
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : item.method === "hybrid"
                        ? "bg-amber-50 text-amber-700 border border-amber-200"
                        : "bg-stone-100 text-stone-600 border border-stone-200"
                  }`}
                >
                  {item.method === "prefab" && (
                    <span className="w-4 h-4 inline-flex items-center justify-center mr-1">
                      <i className="ri-hammer-line text-xs" />
                    </span>
                  )}
                  {item.method === "hybrid" && (
                    <span className="w-4 h-4 inline-flex items-center justify-center mr-1">
                      <i className="ri-stack-line text-xs" />
                    </span>
                  )}
                  {item.method === "traditional" && (
                    <span className="w-4 h-4 inline-flex items-center justify-center mr-1">
                      <i className="ri-building-line text-xs" />
                    </span>
                  )}
                  {methodLabel}
                </span>
              )}
            </div>
            <h3 className="text-white text-xl md:text-2xl font-semibold leading-snug">
              {item.title}
            </h3>
          </div>
        </div>

        <div className="p-6 md:p-8">
          {d?.siteInfo && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
              <div className="bg-cream rounded-xl px-4 py-3 text-center border border-warm-gray/40">
                <p className="text-medium-gray text-sm mb-1">基地面積</p>
                <p className="text-graphite font-semibold">{d.siteInfo.area}</p>
              </div>
              <div className="bg-cream rounded-xl px-4 py-3 text-center border border-warm-gray/40">
                <p className="text-medium-gray text-sm mb-1">所在位置</p>
                <p className="text-graphite font-semibold">{d.siteInfo.location}</p>
              </div>
              <div className="bg-cream rounded-xl px-4 py-3 text-center border border-warm-gray/40">
                <p className="text-medium-gray text-sm mb-1">施工期間</p>
                <p className="text-graphite font-semibold">{d.siteInfo.duration}</p>
              </div>
              <div className="bg-cream rounded-xl px-4 py-3 text-center border border-warm-gray/40">
                <p className="text-medium-gray text-sm mb-1">建築規模</p>
                <p className="text-graphite font-semibold">{d.siteInfo.floors}</p>
              </div>
            </div>
          )}

          {d?.designConcept && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-champagne/10 text-champagne">
                  <i className="ri-lightbulb-line" />
                </span>
                <h4 className="text-graphite font-semibold text-lg">設計概念</h4>
              </div>
              <p className="text-medium-gray leading-relaxed pl-10">
                {d.designConcept}
              </p>
            </div>
          )}

          {d?.constructionHighlights && d.constructionHighlights.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-champagne/10 text-champagne">
                  <i className="ri-hammer-line" />
                </span>
                <h4 className="text-graphite font-semibold text-lg">工法亮點</h4>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {d.constructionHighlights.map((hl, i) => (
                  <div
                    key={i}
                    className="bg-cream rounded-xl p-4 border border-warm-gray/40 transition-all duration-300 hover:border-champagne/30 hover:-translate-y-0.5"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-champagne/10 text-champagne mb-3">
                      <span className="text-xs font-serif font-bold">{String(i + 1).padStart(2, "0")}</span>
                    </span>
                    <p className="text-graphite text-sm font-medium leading-relaxed">
                      {hl}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {d?.ownerFeedback && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-champagne/10 text-champagne">
                  <i className="ri-chat-quote-line" />
                </span>
                <h4 className="text-graphite font-semibold text-lg">業主心得</h4>
              </div>
              <div className="pl-10 border-l-4 border-champagne/30 bg-cream/50 rounded-r-xl p-5">
                <p className="text-graphite italic leading-relaxed">「{d.ownerFeedback}」</p>
              </div>
            </div>
          )}

          {/* Similar Cases */}
          {similarCases.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-champagne/10 text-champagne">
                  <i className="ri-stack-line" />
                </span>
                <h4 className="text-graphite font-semibold text-lg">你可能也會喜歡</h4>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {similarCases.map((sc) => (
                  <button
                    key={sc.id}
                    onClick={() => {
                      // Swap to this case in modal
                      const event = new CustomEvent("portfolio:openDetail", {
                        detail: { itemId: sc.id },
                      });
                      window.dispatchEvent(event);
                    }}
                    className="group text-left bg-cream rounded-xl overflow-hidden border border-warm-gray/40 transition-all duration-300 hover:border-champagne/40 hover:-translate-y-0.5 cursor-pointer"
                  >
                    <div className="relative h-28 overflow-hidden">
                      <img
                        src={sc.image}
                        alt={sc.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-graphite/60 to-transparent" />
                      <span className="absolute top-2 left-2 tag-label text-xs px-2 py-0.5">
                        {sc.tag}
                      </span>
                    </div>
                    <div className="p-3">
                      <p className="text-graphite text-sm font-medium leading-snug line-clamp-2 group-hover:text-champagne transition-colors">
                        {sc.title}
                      </p>
                      <span className="text-xs text-medium-gray mt-1 inline-flex items-center gap-1">
                        <i className="ri-arrow-right-line" />
                        查看詳情
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <a
            href="https://lin.ee/OpssTIM"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary w-full justify-center gap-2 text-base"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-line-fill" />
            </span>
            預約類似案例諮詢
          </a>
        </div>
      </div>
    </div>
  );
}